var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { compareArrays, count, Observable, } from "@configura/web-utilities";
import { CfgProduct } from "../CfgProduct.js";
import { CfgMtrlApplication } from "../material/CfgMtrlApplication.js";
import { CfgMtrlApplicationSource } from "../material/CfgMtrlApplicationSource.js";
import { recursivelyGetPriceCodeValue } from "../utilitiesCatalogueData.js";
import { NumericValuesSelection } from "../utilitiesNumericValues.js";
import { CfgFeature, SelectionType } from "./CfgFeature.js";
import { getMtrlPreview, syncCfgFeatures } from "./utilitiesProductConfiguration.js";
export var ProductConfigurationBubbleMode;
(function (ProductConfigurationBubbleMode) {
    /**
     * If this is select it will turns on all ancestors all the way up.
     */
    ProductConfigurationBubbleMode["BubbleSelected"] = "BubbleSelected";
    /**
     * Bubble to the closest CfgProduct, let it revalidate, then that will continue the bubble
     * after validate.
     */
    ProductConfigurationBubbleMode["Validate"] = "Validate";
    /**
     * Bubble to the closest CfgProduct, let it revalidate, then that will continue the bubble
     * after validate. If this is select it will turn on all ancestors all the way up.
     * So with this mode it is possible to select an option where its parents are not selected.
     */
    ProductConfigurationBubbleMode["ValidateAndBubbleSelected"] = "ValidateAndBubbleSelected";
    /**
     * Like ValidateAndBubbleSelected, but SyncGroups are applied after ValidateAndBubbleSelected
     * has been done
     */
    ProductConfigurationBubbleMode["ValidateAndBubbleSelectedAndApplySyncGroups"] = "ValidateAndBubbleSelectedAndApplySyncGroups";
    /**
     * Stop bubbling
     * This mode supports internal functionality and is not expected to be used by integrators.
     */
    ProductConfigurationBubbleMode["Stop"] = "Stop";
    /**
     * Bubble to the next level up the tree. In features-options the next level is considered
     * the next feature, so option levels are skipped over. The node we call from notifies its
     * parent, and the parent switches out the reference to the node. Then bubbling stops.
     * This mode supports internal functionality and is not expected to be used by integrators.
     */
    ProductConfigurationBubbleMode["OneLevel"] = "OneLevel";
    /**
     * Bubble to the closest CfgProduct without doing any validation
     * This mode supports internal functionality and is not expected to be used by integrators.
     */
    ProductConfigurationBubbleMode["ToParentProduct"] = "ToParentProduct";
    /**
     * Bubble to the root CfgProduct
     * This mode supports internal functionality and is not expected to be used by integrators.
     */
    ProductConfigurationBubbleMode["ToRoot"] = "ToRoot";
})(ProductConfigurationBubbleMode || (ProductConfigurationBubbleMode = {}));
/** Overlap in one child also count */
function doesChildrenShareOptionsCode(features) {
    const optionCodeSet = new Set();
    for (const feature of features) {
        for (const option of feature.options) {
            const code = option.code;
            if (optionCodeSet.has(code)) {
                return true;
            }
            optionCodeSet.add(code);
        }
    }
    return false;
}
/**
 * This class is only meant to be used through CfgOption. It should never be instantiated on its
 * own. Normally the internal state of this class should never be directly modified. CfgOption is
 * the class that should be used and interacted with.
 */
export class _CfgOptionInternal {
    constructor(rawOption, rawFeatures, siblingHasDuplicateDescription, parent, parentConfiguration, parentProduct, rootProduct) {
        this.rawOption = rawOption;
        this.rawFeatures = rawFeatures;
        this.parent = parent;
        this.parentConfiguration = parentConfiguration;
        this.parentProduct = parentProduct;
        this.rootProduct = rootProduct;
        this.changeObservable = new Observable();
        /** Called by child to tell its parent that it has changed. */
        this._childHasChanged = (freshRef, bubbleMode, committed) => __awaiter(this, void 0, void 0, function* () {
            const features = this._features;
            if (features === undefined) {
                throw Error("Child says it changed, but no children has actually been generated");
            }
            const i = features.findIndex((a) => a.isBackedBySame(freshRef));
            if (i === -1) {
                throw Error("Child feature not found");
            }
            features[i] = freshRef;
            // In CfgOption we let stop bubble slip through. This is because CfgOption is sort of
            // a semi level. Like Feature + Option together constitutes one level.
            if (bubbleMode === ProductConfigurationBubbleMode.ValidateAndBubbleSelected ||
                bubbleMode === ProductConfigurationBubbleMode.BubbleSelected) {
                // selectOption takes care of the bubble
                yield this.parent.selectOption(this, true, bubbleMode);
            }
            else {
                yield this.parent._childHasChanged(this, bubbleMode, committed);
            }
        });
        this.getDtoConf = (includeExtendedData) => {
            const { features, isUseNumericValue, code, selected, numericValue } = this;
            if (!selected) {
                throw new Error("Currently only useable on selected options. Selected in the result is for future use.");
            }
            const result = {
                code,
                selected, // For future use
            };
            if (isUseNumericValue) {
                if (numericValue === undefined) {
                    throw new Error("numeric Sku feature with option without numeric value");
                }
                else {
                    result.numericValue = { value: numericValue, unit: this.unit };
                }
            }
            if (0 < features.length) {
                result.features = features.map((f) => f._internal.getDtoConf(includeExtendedData));
            }
            return result;
        };
        // DtoOptionConf is the newer more easily readable format for configuration. As
        // the API:s are still using the older format (and will for the forseeable future) we need
        // to support both formats. The new format can be converted to the old, but not the other
        // way around. For that reason the get-method above uses the new format, and the set-method
        // below the old format. As these functions are meant to only be used internally this should't
        // cause too much confusion.
        this.setApiSelection = (apiOptionSelection, apiOptionConstraint) => __awaiter(this, void 0, void 0, function* () {
            let change = false;
            const upcharge = this._calculateUpcharge();
            if (this._upcharge !== upcharge) {
                change = true;
                this._upcharge = upcharge;
            }
            let features;
            if (apiOptionSelection === undefined) {
                features = this._features || []; // All already generated children
            }
            else {
                features = this.features; // This will generate all children
            }
            if ((yield Promise.all(features.map((f) => f._internal.setApiSelection(apiOptionSelection ? apiOptionSelection.next : undefined, apiOptionConstraint === null || apiOptionConstraint === void 0 ? void 0 : apiOptionConstraint.next)))).some((b) => b)) {
                change = true;
            }
            return change;
        });
        this.getApiConstrained = (constrOptions) => {
            var _a;
            const next = {};
            // use _features to avoid populating when the option is not selected
            for (const feature of (_a = this._features) !== null && _a !== void 0 ? _a : []) {
                feature._internal.addForApiConstrained(next);
            }
            return {
                code: this.code,
                feature: this.parent.code,
                constrOptions: constrOptions.map((o) => o.code),
                next,
            };
        };
        this.structureCompare = (other, strictOrder = true, descriptionMatch = false) => this.keyMatch(other, descriptionMatch) &&
            compareArrays(this.features, other.features, (l, r) => l._internal.structureCompare(r._internal, strictOrder, descriptionMatch), strictOrder);
        this.tryMatchSelection = (other, descriptionMatch = false) => __awaiter(this, void 0, void 0, function* () {
            return (yield Promise.all(other.features.map((otherF) => (() => __awaiter(this, void 0, void 0, function* () {
                if (1 <
                    count(other.features, (item) => item._internal.keyMatch(otherF._internal, descriptionMatch))) {
                    console.warn("tryMatchSelection will ignore items with same key");
                    return false;
                }
                const toSetFeatures = this.features.filter((f) => f._internal.keyMatch(otherF._internal, descriptionMatch));
                if (1 < toSetFeatures.length) {
                    console.warn("tryMatchSelection will ignore items with same key");
                    return false;
                }
                if (toSetFeatures.length === 0) {
                    return false;
                }
                return yield toSetFeatures[0]._internal.tryMatchSelection(otherF._internal, descriptionMatch);
            }))()))).some((b) => b);
        });
        this.keyMatch = (other, descriptionMatch = false) => descriptionMatch
            ? this.description.toLowerCase() === other.description.toLowerCase()
            : this.code === other.code;
        this._getFeaturesWithCode = (code) => this.features.reduce((agg, feature) => {
            agg.push(...feature._internal._getFeaturesWithCode(code));
            return agg;
        }, []);
        // Description based key helps when switching between products with similar feature-options
        // tree and trying to retain made selection.
        this.key =
            this.description +
                (this.description === "" || siblingHasDuplicateDescription ? this.code : "");
        this._upcharge = this._calculateUpcharge();
        const rawRanges = rawOption.codeRanges;
        if (rawRanges === undefined || rawRanges.length === 0) {
            if (this.isUseNumericValue) {
                throw new Error("Options in numeric sku Features must have at lease one code range");
            }
        }
        else {
            this.allowedNumericValues = new NumericValuesSelection(rawRanges);
            this._numericValue = this.allowedNumericValues.first;
        }
    }
    isAllowedNumericValue(val) {
        const allowedNumericValues = this.allowedNumericValues;
        if (allowedNumericValues === undefined) {
            return false;
        }
        if (this.isUseNumericValue) {
            return allowedNumericValues.includesValue(val);
        }
        // There is some limited support for numeric values on non numeric features.
        // The first value is always used in these cases.
        return allowedNumericValues.first === val;
    }
    get code() {
        return this.rawOption.code;
    }
    get notes() {
        var _a;
        return this.parentProduct.getNotes((_a = this.rawOption.noteRefs) !== null && _a !== void 0 ? _a : []);
    }
    get miscFiles() {
        var _a;
        return (_a = this.rawOption.miscFiles) !== null && _a !== void 0 ? _a : [];
    }
    get isUseNumericValue() {
        return this.parent.isUseNumericValue;
    }
    get numericValue() {
        return this._numericValue;
    }
    setNumericValue(val, doSelectOption) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.isAllowedNumericValue(val)) {
                throw new Error(`The value ${val} is not allowed. This could be because: 1. There are no allowed ranges defined 2. The Feature is numeric selection and the value does not fit in the allowed values 3. The Feature is not numeric selection and the value is not the first in the allowed ranges.`);
            }
            let change = false;
            if (this.isUseNumericValue) {
                if (this._numericValue !== val) {
                    this._numericValue = val;
                    change = true;
                }
                // It could be that even though our value did not change some sibling value did, and
                // this could make it needed to bubble later. Maybe. A bit uncertain about why I did
                // not put this in the if-statement above //Linus
                if (yield this.parent.pushStretch()) {
                    change = true;
                }
            }
            if (doSelectOption) {
                if (yield this.parent.selectOption(this, true, ProductConfigurationBubbleMode.Validate)) {
                    change = true;
                }
            }
            else {
                if (change) {
                    yield this.parent._childHasChanged(this, ProductConfigurationBubbleMode.Stop, false);
                }
            }
            return change;
        });
    }
    get unit() {
        return this.parent.unit;
    }
    get description() {
        return this.rawOption.description;
    }
    get selected() {
        return this.parent.isSelected(this);
    }
    get disabled() {
        return this.parent.isDisabled(this);
    }
    get selectedChangeInProgress() {
        const syncGroupHandler = this.rootProduct.syncGroupHandler;
        if (syncGroupHandler === undefined) {
            return false;
        }
        const inProgressOption = syncGroupHandler.pending;
        if (inProgressOption === this) {
            return true;
        }
        if (!(this.selected && this.parent.selectionType === SelectionType.SelectOne)) {
            return false;
        }
        return this.parent.options.some((o) => o._internal === inProgressOption);
    }
    get ancestorsSelected() {
        return this.selected && this.parent.ancestorsSelected;
    }
    get mtrlApplications() {
        if (this._mtrlApplications === undefined) {
            this._mtrlApplications = (this.rawOption.mtrlApplications || []).map((m) => CfgMtrlApplication.fromMtrlLikeApplication(CfgMtrlApplicationSource.Option, m));
        }
        return this._mtrlApplications;
    }
    get thumbnail() {
        return this.rawOption.material || getMtrlPreview(this.mtrlApplications);
    }
    _calculateUpcharge() {
        let upcharge = this.rawOption.upcharge || 0;
        const priceCodes = this.rawOption.priceCodes || [];
        const prices = this.rootProduct.prices;
        upcharge += recursivelyGetPriceCodeValue(priceCodes, prices) || 0;
        return upcharge;
    }
    get upcharge() {
        return this._upcharge;
    }
    get priceChangeAtSelectChange() {
        if (!this.parent.hasUpcharge) {
            return 0;
        }
        const upcharge = this._upcharge || 0;
        const isSelected = this.parent.isSelected(this);
        if (this.parent.selectionType === SelectionType.SelectMany) {
            return isSelected ? -upcharge : upcharge;
        }
        if (this.parent.selectionType === SelectionType.SelectOne) {
            if (isSelected) {
                return 0;
            }
            const selectedOptions = this.parent.selectedOptions;
            if (1 !== selectedOptions.length) {
                console.warn("More or less than one selected in select one");
                return upcharge;
            }
            return upcharge - (selectedOptions[0].upcharge || 0);
        }
        return 0;
    }
    get features() {
        if (this._features === undefined) {
            const allRefs = this.rawOption.featureRefs || [];
            const features = syncCfgFeatures(allRefs, [], this.rawFeatures, this, this.parentConfiguration, this.parentProduct, this.rootProduct);
            if (doesChildrenShareOptionsCode(features)) {
                throw new Error("Stage does not yet properly support Options that has multiple sub-features with overlapping option codes.");
            }
            this._features = features;
        }
        return this._features;
    }
}
export class CfgOption {
    /**
     * Private constructor and make-method because make new ref requires the constructor to
     * take an internal and we don't want those who instantiate CfgOption to have to be aware
     * of the internal.
     */
    constructor(_internal) {
        this._internal = _internal;
        this.isBackedBySame = (other) => this._internal === other._internal;
        this.setNumericValue = (val, doSelectOption) => __awaiter(this, void 0, void 0, function* () { return yield this._internal.setNumericValue(val, doSelectOption); });
        this.isAllowedNumericValue = (val) => this._internal.isAllowedNumericValue(val);
        /**
         * Selects this Option.
         * Only Options belonging to Features that are "select many" can be deselected.
         * Calling this will cause a validation call to the server.
         */
        this.setSelected = (on) => __awaiter(this, void 0, void 0, function* () {
            return yield this._internal.parent.selectOption(this._internal, on, ProductConfigurationBubbleMode.ValidateAndBubbleSelectedAndApplySyncGroups);
        });
        this.listenForChange = (l) => this._internal.changeObservable.listen(l);
        this.stopListenForChange = (l) => this._internal.changeObservable.stopListen(l);
    }
    static make(rawOption, rawFeatures, siblingHasDuplicateDescription, parent, parentConfiguration, parentProduct, rootProduct) {
        return new this(new _CfgOptionInternal(rawOption, rawFeatures, siblingHasDuplicateDescription, parent, parentConfiguration, parentProduct, rootProduct));
    }
    /**
     * Makes an object wrapping the passed object. This is not a clone method,
     * it is a method to make a new outer reference. Like a shallow copy.
     * We use this to help frameworks that are build around using equals to detect change.
     */
    static _makeNewRefFrom(internal) {
        return new this(internal);
    }
    get parentProduct() {
        return CfgProduct._makeNewRefFrom(this._internal.parentProduct);
    }
    get parent() {
        return CfgFeature._makeNewRefFrom(this._internal.parent);
    }
    get rootProduct() {
        return CfgProduct._makeNewRefFrom(this._internal.rootProduct);
    }
    get rawOption() {
        return this._internal.rawOption;
    }
    get key() {
        return this._internal.key;
    }
    get code() {
        return this._internal.code;
    }
    get notes() {
        return this._internal.notes;
    }
    get miscFiles() {
        return this._internal.miscFiles;
    }
    get isUseNumericValue() {
        return this._internal.isUseNumericValue;
    }
    get numericValue() {
        return this._internal.numericValue;
    }
    get allowedNumericValues() {
        return this._internal.allowedNumericValues;
    }
    get unit() {
        return this._internal.unit;
    }
    get description() {
        return this._internal.description;
    }
    get selected() {
        return this._internal.selected;
    }
    get disabled() {
        return this._internal.disabled;
    }
    /**
     * Selection state is in progress to be changed. This can be used in GUI
     * to display the state as transitioning, or as already changed.
     * If selectedChangeInProgress and:
     * selected is true, it means that this is about to get unselected
     * selected is false, it means that this is about to get selected
     */
    get selectedChangeInProgress() {
        return this._internal.selectedChangeInProgress;
    }
    /** Are all ancestors up to the CfgProductConfiguration selected? Includes self. */
    get ancestorsSelected() {
        return this._internal.ancestorsSelected;
    }
    get thumbnail() {
        return this._internal.thumbnail;
    }
    get upcharge() {
        return this._internal.upcharge;
    }
    get priceChangeAtSelectChange() {
        return this._internal.priceChangeAtSelectChange;
    }
    get features() {
        return this._internal.features;
    }
}
